import { useAuth } from '../../../context/AuthContext';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Upload, FileSpreadsheet, AlertTriangle, CheckCircle2, Loader2, Info } from 'lucide-react';
import { CARD, SectionHeading, TH } from './trackerUi';
import { TrackerSection } from './TrackerSection';
import { DatePickerField } from '../../../components/common/DatePickerField';
import {
  useSnapshotUpload,
  UPLOADABLE_REPORT_TYPES,
  REPORT_LIMITS,
  REPORT_DESTINATION,
  REFUSAL_GUIDANCE,
  hasAcceptedExtension,
  encodedCeilingFor,
  UploadableReportType,
} from '../../../hooks/pipelineTracker/useSnapshotUpload';
import type { UploadHistoryRow } from '../../../hooks/pipelineTracker/useSnapshotUpload';

/**
 * Weekly export upload.
 *
 * The ingestion callable was complete and stage-verified for three days while
 * this tab said "Landing next", so no tenant could reach it. Everything here
 * exists to get a real file to that callable and to explain, in the operator's
 * terms, what happened.
 *
 * WHAT THE OPERATOR NEEDS TOLD, and the reason it is worth the pixels: the Task
 * Summary export is ONE workbook whose other five sheets are outputs this
 * tracker computes for itself. They are not extra uploads and there is no ZIP.
 * That misunderstanding is what the previous refusal actively encouraged.
 *
 * NO FILE CONTENT IS SHOWN OR RETAINED. Row counts and refusal codes only.
 */

const PHASE_LABEL: Record<string, string> = {
  reading: 'Reading the file',
  validating: 'Checking size and format',
  submitting: 'Sending to the server',
  processing: 'Checking columns and publishing',
};

const STATE_STYLE: Record<string, string> = {
  committed: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30',
  superseded: 'bg-gray-500/10 text-gray-300 border-gray-500/30',
  processing: 'bg-blue-500/10 text-blue-300 border-blue-500/30',
  failed: 'bg-red-500/10 text-red-300 border-red-500/30',
};

const mb = (bytes: number) => `${Math.round(bytes / (1024 * 1024))} MB`;

/**
 * Who submitted this upload, in the only two terms a tenant should ever see.
 *
 * A white-glove upload is performed by Valutten on the tenant's behalf, and the
 * server marks it three ways: `uploadedBy: 'Valutten'`, the system email, and
 * the `system` uploader role. Any one of them is enough, so legacy manifests
 * written before a field existed still read correctly. This mirrors the actor
 * test in `pages/admin/AuditLogPage.tsx`.
 *
 * A raw uid or a real person's email is never rendered here.
 */
export function uploaderLabel(row: Pick<UploadHistoryRow, 'uploadedBy' | 'uploadedByEmail' | 'uploaderRole'>): string {
  if (row.uploadedBy === 'Valutten') return 'VALUTTEN';
  if (row.uploadedByEmail === 'system@valutten.com') return 'VALUTTEN';
  if (row.uploaderRole === 'system') return 'VALUTTEN';
  return 'Your team';
}

const formatWhen = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  // Brisbane, because that is the clock the operator and the date guard use.
  return d.toLocaleString('en-AU', { timeZone: 'Australia/Brisbane', dateStyle: 'medium', timeStyle: 'short' });
};

const UploadTab: React.FC<{ companyId: string }> = ({ companyId }) => {
  const { isFounder } = useAuth();
  if (isFounder) return <TrackerSection id="upload" step="1 · Upload" title="Add an export and see what arrived">
    <section className={`${CARD} p-5`}>
      <SectionHeading icon={Info} title="Uploads and history" />
      <p className="text-sm text-gray-400">View upload history and upload exports from the company’s Pipeline Tracker section in Admin. Founder view is read-only.</p>
    </section>
  </TrackerSection>;
  return <TenantUploadTab companyId={companyId} />;
};

const TenantUploadTab: React.FC<{ companyId: string }> = ({ companyId }) => {
  const { phase, refusal, outcome, history, historyLoading, historyError, submit, reset } =
    useSnapshotUpload(companyId);

  const [reportType, setReportType] = useState<UploadableReportType>('performance');
  const [asAtDate, setAsAtDate] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const busy = phase === 'reading' || phase === 'validating' || phase === 'submitting' || phase === 'processing';

  // Brisbane today, matching the server guard exactly. A UTC "today" would
  // refuse a legitimate early morning upload for ten hours of every day.
  const todayBrisbane = useMemo(
    () => new Date(Date.now() + 10 * 60 * 60 * 1000).toISOString().slice(0, 10),
    [],
  );

  /**
   * Everything checkable before sending is checked before sending. The server
   * re-checks all of it; doing it here means the operator is told immediately
   * rather than after a multi-megabyte round trip.
   */
  const problem = useMemo((): string | null => {
    if (!file) return null;
    if (!hasAcceptedExtension(file.name)) return 'Upload a .csv or .xlsx file.';
    const limits = REPORT_LIMITS[reportType];
    if (file.size > limits.maxBytes) {
      return `That file is ${mb(file.size)}. The ${limits.label} limit is ${mb(limits.maxBytes)}.`;
    }
    // The server also bounds the ENCODED length, and base64 adds about a third.
    if (Math.ceil((file.size * 4) / 3) > encodedCeilingFor(reportType)) {
      return `That file is too large once encoded for sending. The ${limits.label} limit is ${mb(limits.maxBytes)}.`;
    }
    if (!asAtDate) return null;
    if (asAtDate > todayBrisbane) return 'The as-at date cannot be in the future.';
    return null;
  }, [file, reportType, asAtDate, todayBrisbane]);

  const canSubmit = Boolean(file && asAtDate && !problem && !busy);

  const choose = useCallback((f: File | null) => {
    setFile(f);
    reset();
  }, [reset]);

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f) choose(f);
  }, [choose]);

  const onSubmit = useCallback(async () => {
    if (!file || !asAtDate) return;
    const ok = await submit(file, reportType, asAtDate);
    if (ok) {
      setFile(null);
      if (inputRef.current) inputRef.current.value = '';
    }
  }, [file, asAtDate, reportType, submit]);

  const guidance = refusal?.refusalCode ? REFUSAL_GUIDANCE[refusal.refusalCode] : undefined;

  return (
    <TrackerSection
      id="upload"
      step="1 · Upload"
      title="Add an export and see what arrived"
      description="Each upload is checked for size, format and columns before anything is published."
    >
      <section className={`${CARD} p-6`}>
        <div className="mb-4">
          <SectionHeading icon={Upload} title="Upload a weekly export" />
          <div>
            <p className="mt-1 text-sm text-gray-400">
              One Performance file and one Task file per weekly cycle. Sales and Compliance are
              ad hoc: upload them when you have one, and their age is shown rather than chased.
              The Task export is a single workbook, and its other sheets are summaries this
              tracker builds for you, so there is nothing else to send and no need to zip anything.
            </p>
            {/*
              Where each upload SHOWS UP. The report names here are Salestrekker's
              names, and none of them is the name of a tab, so without this the
              operator picks "Performance", uploads it, and has to guess which tab
              moved. That was the first question asked by someone using this
              screen who had not built it.
            */}
            <p className="mt-2 text-xs text-gray-500">
              {UPLOADABLE_REPORT_TYPES.map((k, i) => (
                <React.Fragment key={k}>
                  {i > 0 && ' '}
                  {REPORT_LIMITS[k].label} feeds{' '}
                  <b className="text-gray-400">{REPORT_DESTINATION[k]}</b>.
                </React.Fragment>
              ))}
            </p>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-gray-400">Report</span>
            <select
              value={reportType}
              onChange={(e) => { setReportType(e.target.value as UploadableReportType); reset(); }}
              disabled={busy}
              className="w-full rounded-md border border-gray-600 bg-gray-900 px-3 py-2 text-sm text-gray-200 disabled:opacity-50"
            >
              {UPLOADABLE_REPORT_TYPES.map((k) => (
                <option key={k} value={k}>{REPORT_LIMITS[k].label}</option>
              ))}
            </select>
          </label>

          {/*
            The house picker, not the browser's native date input.
            react-day-picker with the app's dark styling, the same control used
            on the client drawer and contact panel, so the one date the operator
            enters here looks like every other date in the product. It emits
            YYYY-MM-DD, which is exactly what asAtDate holds and what the
            callable expects, so nothing has to parse a localised string.

            disableFuture rather than a max attribute: the native max is a hint
            a browser may ignore, while this refuses the click. The server bound
            is still authoritative and is checked in `problem` below.
          */}
          <div className="block">
            <DatePickerField
              id="pipeline-tracker-as-at"
              label="As at date"
              value={asAtDate}
              onChange={(next) => { setAsAtDate(next); reset(); }}
              disabled={busy}
              disableFuture
              placeholder="Select the export date"
            />
            <span className="mt-1 block text-xs text-gray-500">The date the export was taken, not today.</span>
          </div>
        </div>

        <div
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          className={`mt-4 rounded-lg border-2 border-dashed p-6 text-center transition-colors ${
            dragging ? 'border-blue-500 bg-blue-500/5' : 'border-gray-600'
          }`}
        >
          <FileSpreadsheet className="mx-auto mb-2 h-7 w-7 text-gray-500" />
          {file ? (
            <p className="text-sm text-gray-200">{file.name} <span className="text-gray-500">({mb(file.size)})</span></p>
          ) : (
            <p className="text-sm text-gray-400">Drop the export here, or choose a file.</p>
          )}
          <input
            ref={inputRef}
            type="file"
            accept=".csv,.xlsx"
            disabled={busy}
            onChange={(e) => choose(e.target.files?.[0] ?? null)}
            className="sr-only"
          />
          {/*
            The input is visually hidden and driven by this button.

            A bare file input renders the browser's own "No file chosen" label,
            which the drop zone cannot suppress. Drop a file and the zone shows
            its name while the label two lines below still says nothing is
            chosen, so a drop that WORKED reads as one that failed.
          */}
          <button
            type="button"
            disabled={busy}
            onClick={() => inputRef.current?.click()}
            className="mx-auto mt-3 block rounded-md bg-gray-700 px-3 py-1.5 text-xs text-gray-200 hover:bg-gray-600 disabled:opacity-50"
          >
            {file ? 'Choose a different file' : 'Choose file'}
          </button>
          <p className="mt-3 text-xs text-gray-500">
            .csv or .xlsx, up to {mb(REPORT_LIMITS[reportType].maxBytes)} and{' '}
            {REPORT_LIMITS[reportType].maxRows.toLocaleString('en-AU')} rows.
          </p>
        </div>

        {problem && (
          <p className="mt-3 flex items-start gap-2 text-sm text-amber-300">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />{problem}
          </p>
        )}

        <div className="mt-4 flex items-center gap-3">
          <button
            type="button"
            onClick={onSubmit}
            disabled={!canSubmit}
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Upload
          </button>
          {busy && (
            <span className="flex items-center gap-2 text-sm text-gray-400">
              <Loader2 className="h-4 w-4 animate-spin" />
              {PHASE_LABEL[phase]}
            </span>
          )}
        </div>

        {phase === 'completed' && outcome && (
          <div className="mt-4 rounded-md border border-emerald-500/30 bg-emerald-500/10 p-4">
            <p className="flex items-center gap-2 text-sm font-medium text-emerald-300">
              <CheckCircle2 className="h-4 w-4" />Published
            </p>
            <p className="mt-1 text-sm text-emerald-200/80">
              {outcome.committedRowCount.toLocaleString('en-AU')} rows committed
              {outcome.countsVersion === 2 && outcome.rejectedRowCount > 0 && `, ${outcome.rejectedRowCount.toLocaleString('en-AU')} skipped`}
              {outcome.countsVersion !== 2 && ', legacy rejection counts'}
              {outcome.warnings > 0 && `, ${outcome.warnings} warning${outcome.warnings === 1 ? '' : 's'}`}.
            </p>
            {outcome.ambiguousDateColumns && outcome.ambiguousDateColumns.length > 0 && (
              <p className="mt-1 text-xs text-emerald-200/70">
                Ambiguous date format in: {outcome.ambiguousDateColumns.join(', ')}.
              </p>
            )}
          </div>
        )}

        {phase === 'failed' && refusal && (
          <div className="mt-4 rounded-md border border-red-500/30 bg-red-500/10 p-4">
            <p className="flex items-center gap-2 text-sm font-medium text-red-300">
              <AlertTriangle className="h-4 w-4" />
              {guidance?.title ?? 'That upload was not accepted'}
            </p>
            <p className="mt-1 text-sm text-red-200/80">{guidance?.guidance ?? refusal.message}</p>
            {guidance?.showsHeaders && refusal.headers.length > 0 && (
              <ul className="mt-2 flex flex-wrap gap-1.5">
                {refusal.headers.map((h) => (
                  <li key={h} className="rounded bg-red-500/15 px-2 py-0.5 font-mono text-xs text-red-200">{h}</li>
                ))}
              </ul>
            )}
            <p className="mt-2 text-xs text-red-200/60">Nothing was saved from this file.</p>
          </div>
        )}
      </section>

      <section className={`${CARD} p-6`}>
        <SectionHeading icon={Info} title="Recent uploads" />
        <p className="mb-4 flex items-center gap-1.5 text-xs text-gray-500">
          <Info className="h-3.5 w-3.5" />
          Upload history contains row counts and outcomes. Report fields are stored separately. The original file is kept securely by VALUTTEN, is not visible in the app, and is used only to reprocess your reports.
        </p>

        {historyLoading && <p className="text-sm text-gray-400">Loading…</p>}
        {historyError && <p className="text-sm text-amber-300">{historyError}</p>}
        {!historyLoading && !historyError && history.length === 0 && (
          <p className="text-sm text-gray-400">No uploads yet.</p>
        )}

        {history.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="text-xs uppercase tracking-wider text-gray-400">
                <tr>
                  <TH>Report</TH>
                  <TH>As at</TH>
                  <TH>State</TH>
                  <TH>Uploaded by</TH>
                  <TH right>Committed</TH>
                  <TH right>Skipped</TH>
                  <TH>Submitted</TH>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700/60">
                {history.map((row) => (
                  <tr key={row.uploadId} className="text-gray-300">
                    <td className="py-2 pr-4">{row.reportType ?? '-'}</td>
                    <td className="py-2 pr-4 tabular-nums">{row.asAtDate ?? '-'}</td>
                    <td className="py-2 pr-4">
                      <span className={`inline-block rounded border px-1.5 py-0.5 text-xs ${STATE_STYLE[row.state ?? ''] ?? 'border-gray-600 text-gray-400'}`}>
                        {row.state ?? 'unknown'}
                      </span>
                      {row.failureCode && (
                        <span className="ml-2 font-mono text-xs text-red-300/80">{row.failureCode}</span>
                      )}
                    </td>
                    <td className="py-2 pr-4">{uploaderLabel(row)}</td>
                    <td className="py-2 pr-4 text-right tabular-nums">
                      {row.committedRowCount?.toLocaleString('en-AU') ?? '-'}
                    </td>
                    <td className="py-2 pr-4 text-right tabular-nums">
                      {row.countsVersion === 2 ? row.rejectedRowCount?.toLocaleString('en-AU') ?? '-' : 'Legacy count'}
                    </td>
                    <td className="py-2 text-gray-400">{formatWhen(row.createdAt) || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </TrackerSection>
  );
};

export default UploadTab;
